import React, { useState } from "react";
import { Workflow } from "../../types/workflow";
import { ScheduledWorkflowCard, WorkflowEmptyState } from "../dashboard";
import { UpgradeBanner } from "../common";
import UpgradeModal from "../common/UpgradeModal";
import { FiPlus, FiLock } from "react-icons/fi";

const PlusIcon = FiPlus as React.FC<React.SVGProps<SVGSVGElement>>;
const LockIcon = FiLock as React.FC<React.SVGProps<SVGSVGElement>>;

interface WorkflowsTabProps {
  workflows: Workflow[];
  isLoading: boolean;
  isFreePlan: boolean;
  onCreateWorkflow: () => void;
  onPauseResume: (workflow: Workflow) => Promise<void>;
  onEdit: (workflow: Workflow) => void;
  onView: (workflow: Workflow) => void;
  onDelete: (workflow: Workflow) => void;
  onViewEmails?: (workflow: Workflow) => void;
}

/**
 * WorkflowsTab - Displays list of active workflows
 * Reuses ScheduledWorkflowCard from dashboard
 */
const WorkflowsTab: React.FC<WorkflowsTabProps> = ({
  workflows,
  isLoading,
  isFreePlan,
  onCreateWorkflow,
  onPauseResume,
  onEdit,
  onView,
  onDelete,
  onViewEmails,
}) => {
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const handlePauseResume = async (workflow: Workflow) => {
    setTogglingId(workflow.id);
    try {
      await onPauseResume(workflow);
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div className="su-emails-tab-content">
      {/* Active Workflows Section */}
      <div className="su-section">
        <div className="su-section-header">
          <div>
            <h2 className="su-text-section-heading">Active Workflows</h2>
            <p className="su-text-subtitle mt-1">Automated recurring updates</p>
          </div>
          <button className="su-btn-primary" onClick={onCreateWorkflow}>
            <PlusIcon className="w-4 h-4" />
            <span>CREATE WORKFLOW</span>
          </button>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="space-y-4 mt-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="su-workflow-card animate-pulse">
                <div className="flex items-center gap-3">
                  <div className="h-5 w-40 bg-gray-800 rounded" />
                  <div className="h-5 w-16 bg-gray-800 rounded" />
                  <div className="h-5 w-20 bg-gray-800 rounded" />
                </div>
                <div className="su-workflow-info-grid-fixed mt-4">
                  <div className="h-8 bg-gray-800 rounded" />
                  <div className="h-8 bg-gray-800 rounded" />
                  <div className="h-8 bg-gray-800 rounded" />
                  <div className="h-8 bg-gray-800 rounded" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && workflows.length === 0 && !isFreePlan && (
          <WorkflowEmptyState onCreateWorkflow={onCreateWorkflow} />
        )}

        {/* Free Plan Empty State */}
        {!isLoading && isFreePlan && (
          <div className="su-workflow-empty-state">
            <div className="su-workflow-empty-icon">
              <LockIcon className="w-12 h-12" />
            </div>
            <h3 className="su-workflow-empty-title">Automated Workflows</h3>
            <p className="su-workflow-empty-subtitle">
              Upgrade to Basic or Pro to create recurring automated email
              updates with AI-generated insights.
            </p>
            <button
              className="su-btn su-btn-purple su-btn-lg su-workflow-empty-cta"
              onClick={() => setShowUpgradeModal(true)}
            >
              Upgrade to Unlock
            </button>
          </div>
        )}

        {/* Workflow List */}
        {!isLoading && workflows.length > 0 && (
          <div className="mt-4">
            {workflows.map((workflow) => (
              <ScheduledWorkflowCard
                key={workflow.id}
                workflow={workflow}
                onPauseResume={() => handlePauseResume(workflow)}
                onEdit={() => onEdit(workflow)}
                onView={() => onView(workflow)}
                onDelete={() => onDelete(workflow)}
                onViewEmails={onViewEmails ? () => onViewEmails(workflow) : undefined}
                isToggling={togglingId === workflow.id}
              />
            ))}
          </div>
        )}
      </div>

      {/* Upgrade Banner for Free Plan Users */}
      {isFreePlan && <UpgradeBanner />}

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />
    </div>
  );
};

export default WorkflowsTab;



